import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/useToast';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientsDropdown, useCreatePayout } from '../../hooks';
import { CreatePayoutRequest, CreatePayoutResponse } from '../../models';

interface CreatePayoutButtonProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CreatePayoutButton = ({ isOpen, onClose }: CreatePayoutButtonProps) => {
    const { t } = useTranslation('codPayouts');
    const [clientId, setClientId] = useState('');
    const [result, setResult] = useState<CreatePayoutResponse | null>(null);
    const { createPayoutAsync, isCreating } = useCreatePayout();
    const { toast } = useToast();
    const { clients, isLoading: isLoadingClients } = useClientsDropdown();

    const handleCreatePayout = async () => {
        if (!clientId) {
            toast({
                title: t('payout.error_title', 'Error al crear liquidación'),
                description: t(
                    'payout.error_client_required',
                    'Debe seleccionar un cliente'
                ),
                variant: 'destructive',
            });
            return;
        }

        try {
            const data: CreatePayoutRequest = {
                client_id: parseInt(clientId, 10),
                currency: 'EUR', // Valor por defecto, el backend maneja la moneda automáticamente
            };

            const resultData = await createPayoutAsync(data);
            setResult(resultData);
            setClientId('');
        } catch (error: any) {
            toast({
                title: t('payout.error_title', 'Error al crear liquidación'),
                description:
                    error.message ||
                    t('payout.error_description', 'No se pudo crear la liquidación'),
                variant: 'destructive',
            });
        }
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setClientId('');
            setResult(null);
        }
    }, [isOpen]);

    const handleClose = () => {
        if (!isCreating) {
            onClose();
        }
    };

    // Convertir clientes a formato de opciones para Combobox
    const clientOptions = useMemo(
        () =>
            clients.map((client) => ({
                label: client.name,
                value: String(client.id),
            })),
        [clients]
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        {t('payout.modal_title', 'Crear Liquidación')}
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {t(
                            'payout.modal_description',
                            'Se creará una liquidación con todos los items pendientes del cliente seleccionado.'
                        )}
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <div className="space-y-4">
                        <Alert variant={result.payouts_created > 0 ? 'default' : 'default'}>
                            {result.payouts_created > 0 ? (
                                <CheckCircle2 className="h-4 w-4" />
                            ) : (
                                <Info className="h-4 w-4" />
                            )}
                            <AlertTitle className="text-foreground">
                                {result.payouts_created > 0
                                    ? t('payout.success_title', 'Liquidación creada')
                                    : t('payout.info_title', 'Información')}
                            </AlertTitle>
                            <AlertDescription className="text-muted-foreground">
                                <div className="space-y-2 mt-2">
                                    <div className="flex justify-between items-center">
                                        <span>Liquidaciones creadas:</span>
                                        <span className={`font-medium ${result.payouts_created > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                                            {result.payouts_created}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span>Pedidos liquidados:</span>
                                        <span className={`font-medium ${result.items_assigned > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                                            {result.items_assigned}
                                        </span>
                                    </div>
                                </div>
                            </AlertDescription>
                        </Alert>

                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={() => {
                                    setResult(null);
                                    setClientId('');
                                    onClose();
                                }}
                            >
                                {t('payout.close', 'Cerrar')}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreatePayout(); }} className="space-y-4">
                        {/* Cliente */}
                        <div className="space-y-2">
                            <Label htmlFor="client" className="text-foreground">
                                {t('payout.client', 'Cliente')} <span className="text-destructive">*</span>
                            </Label>
                            <Combobox
                                options={clientOptions}
                                placeholder={
                                    isLoadingClients
                                        ? t('payout.loading_clients', 'Cargando clientes...')
                                        : t('payout.client_placeholder', 'Buscar cliente...')
                                }
                                value={clientId}
                                setValue={setClientId}
                                disabled={isCreating || isLoadingClients}
                                translations={{
                                    notFound: t('payout.no_clients_found', 'No se encontraron clientes'),
                                }}
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isCreating}
                            >
                                {t('payout.cancel', 'Cancelar')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={isCreating || !clientId}
                            >
                                {isCreating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {isCreating
                                    ? t('payout.creating', 'Creando...')
                                    : t('payout.confirm', 'Crear')}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

